import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { HelpCircle, MessageSquare } from 'lucide-react';

function truncate(text, max = 96) {
  if (!text) return '';
  const t = text.trim();
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

export default function ConfusionBacklogPanel({ logs }) {
  const unresolved = (logs || []).filter((l) => !l.resolved);
  const sorted = [...unresolved].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );

  return (
    <Card className="border-border/60 shadow-sm flex flex-col min-h-[280px]">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-muted-foreground" aria-hidden />
          <CardTitle className="font-serif text-lg font-semibold">Confusion backlog</CardTitle>
        </div>
        <CardDescription>
          Open items you have not marked resolved. Clear them in Q&amp;A or review.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-0">
        {sorted.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-10 rounded-lg border border-dashed border-border/70 bg-muted/20">
            <p className="text-sm text-muted-foreground">No open confusions. Nice.</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              When something does not click during Q&amp;A, log it — it will show up here.
            </p>
          </div>
        ) : (
          <>
            <ScrollArea className="h-[220px] pr-3 -mr-1">
              <ul className="space-y-2">
                {sorted.slice(0, 12).map((log) => (
                  <li
                    key={log.id}
                    className="rounded-lg border border-border/50 bg-card px-3 py-2.5 text-left transition-colors hover:bg-muted/30"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {log.topic || 'General'}
                      </span>
                      {log.created_at && (
                        <time
                          className="text-[10px] text-muted-foreground tabular-nums"
                          dateTime={log.created_at}
                        >
                          {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                        </time>
                      )}
                    </div>
                    <p className="text-sm text-foreground leading-snug">{truncate(log.confusion_text)}</p>
                  </li>
                ))}
              </ul>
            </ScrollArea>
            <Button variant="outline" size="sm" className="mt-4 w-full" asChild>
              <Link to="/qa">
                <MessageSquare className="w-3.5 h-3.5 mr-2" />
                Open Q&amp;A
              </Link>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
